import { useChartTimeNavigation } from './ChartTimeNavigation'
import { memo, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import ReactECharts from 'echarts-for-react'
import type { ECharts, EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerCandle, ScreenerExchangeData, ScreenerOIDataPoint, ScreenerVolumeDataPoint } from '@/types'
import { FundingRateBadge } from './FundingRateBadge'
import { ChartCollapseButton } from './ChartCollapseButton'
import { ChartRuler, type OIRulerSeries } from './ChartRuler'
import { ChartLevelAlerts, chartAlertMarkLine, type ChartAlertControls } from './ChartLevelAlerts'
import { useChartAlertDrag } from './useChartAlertDrag'
import { useVisibleChartScale } from './useVisibleChartScale'
import { useChartResize } from './useChartResize'
import { chartTooltipTime, useChartCrosshair } from './useChartCrosshair'
import { buildOIPlot, filledOIDescription, isDisplayableOI } from './oiDisplay'
import { oiValueAxisPointer } from './oiCrosshair'
import { buildVolumeLookup, volumeAtTime, volumeTooltipRow } from './volumeDisplay'
import {
  findLastKnownIndex,
  formatAge,
  formatCompact,
  formatUtc,
  formatUtcAxis,
  isFreshOI,
  isPlottableOI,
  lastKnownPoint,
  oiQualityDescription,
} from './chartUtils'

interface OIPanelProps {
  symbol: string
  oiData: ScreenerOIDataPoint[]
  candles: ScreenerCandle[]
  currentPrice?: number | null
  candleInterval?: string
  currentOI: number | null
  fundingData?: Partial<ScreenerExchangeData>
  volumeHistory?: ScreenerVolumeDataPoint[]
  volumeInterval?: string
  exchange: string
  interval: string
  /** Accepted for older callers; OI charts always render as lines. */
  lineMode?: boolean
  fillThrough?: number | null
  color: string
  chartGroup: string
  onCrosshairMove: (time: Time | null) => void
  syncedTime: Time | null
  showXAxis?: boolean
  height?: number
  orderControls?: ReactNode
  alertControls?: ChartAlertControls
  collapsed?: boolean
  onToggleCollapse?: () => void
}

function OIPanelComponent({
  symbol,
  oiData,
  candles,
  currentPrice,
  candleInterval,
  currentOI,
  fundingData,
  volumeHistory,
  volumeInterval,
  exchange,
  interval,
  fillThrough,
  color,
  chartGroup,
  onCrosshairMove,
  syncedTime,
  showXAxis = true,
  height = 145,
  orderControls,
  alertControls: savedAlertControls,
  collapsed = false,
  onToggleCollapse,
}: OIPanelProps) {
  const chartBodyId = useId()
  const chartRef = useRef<ReactECharts>(null)
  const [rulerChart, setRulerChart] = useState<ECharts | null>(null)
  useChartResize(rulerChart)
  const timeNavigation = useChartTimeNavigation(rulerChart)

  const derived = useMemo(() => {
    const plot = buildOIPlot(oiData, interval, fillThrough)
    return {
      ...plot,
      timestamps: plot.points.map((point) => point.timestamp * 1000),
    }
  }, [oiData, interval, fillThrough])

  const volumeLookup = useMemo(() => buildVolumeLookup(volumeHistory, volumeInterval, interval),
    [volumeHistory, volumeInterval, interval])

  const scaleSeries = useMemo(() => [derived.series], [derived])
  const alertSeries = useMemo(() => [{ exchange, points: derived.series }], [exchange, derived])
  const alertControls = useChartAlertDrag(rulerChart, savedAlertControls, { symbol, metric: 'oi', exchanges: [exchange] })
  const { fitVisibleScale, onChartReady } = useVisibleChartScale(scaleSeries, `${symbol}:${exchange}:${interval}`, true)

  const rulerSeries = useMemo<OIRulerSeries[]>(() => [{
    id: exchange, label: exchange, color,
    points: oiData.filter(isPlottableOI),
    oiData, candles, currentPrice,
  }], [exchange, color, oiData, candles, currentPrice])

  const hovered = useMemo(
    () => lastKnownPoint(derived.points, syncedTime == null ? null : Number(syncedTime)),
    [derived.points, syncedTime],
  )
  const displayValue = hovered.point && isDisplayableOI(hovered.point) ? hovered.point.value : syncedTime == null ? currentOI : null

  const option = useMemo<EChartsOption>(() => ({
    animation: false,
    useUTC: true,
    backgroundColor: 'transparent',
    grid: {
      left: 10,
      right: 74,
      top: 6,
      bottom: showXAxis ? 28 : 8,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', lineStyle: { color: '#64748b', type: 'dashed' } },
      backgroundColor: 'rgba(5, 10, 18, 0.96)',
      borderColor: '#334155',
      textStyle: { color: '#e2e8f0', fontSize: 11 },
      formatter: (raw: any) => {
        const params = Array.isArray(raw) ? raw : [raw]
        const axisMs = chartTooltipTime(chartRef.current?.getEchartsInstance(), Number(params[0]?.axisValue ?? 0))
        const index = findLastKnownIndex(derived.timestamps, axisMs)
        const point = index >= 0 ? derived.points[index] : null
        const volumeRow = volumeTooltipRow(volumeAtTime(volumeLookup, axisMs, interval), interval)
        if (!point || !isDisplayableOI(point)) return `<div>${formatUtc(axisMs, interval === '5s')}</div><div>OI: N/A</div>${volumeRow}`
        const age = Math.max(0, Math.floor((axisMs - (point.filledFromTimestamp ?? point.timestamp) * 1000) / 1000))
        const qualityDescription = filledOIDescription(point) || oiQualityDescription(point)
        return `
          <div style="color:#94a3b8;margin-bottom:5px">${formatUtc(axisMs, interval === '5s')}</div>
          <div>${exchange.toUpperCase()} OI: <b style="color:${color}">${formatCompact(point.value)} coins</b></div>
          ${volumeRow}
          ${qualityDescription ? `<div style="color:${isFreshOI(point) ? '#94a3b8' : '#f59e0b'};font-size:10px">${qualityDescription}</div>` : ''}
          ${age > 0 ? `<div style="color:#f59e0b;font-size:10px">Last sample ${formatAge(age)}</div>` : ''}
        `
      },
    },
    xAxis: {
      type: 'time',
      axisPointer: { snap: false },
      ...timeNavigation.axis,
      axisLine: { show: showXAxis, lineStyle: { color: '#253044' } },
      axisTick: { show: false },
      axisLabel: {
        show: showXAxis,
        color: '#64748b',
        fontSize: 9,
        hideOverlap: true,
        formatter: (value: number) => formatUtcAxis(value, interval),
      },
      splitLine: { show: true, lineStyle: { color: '#172033', type: 'dashed' } },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      triggerEvent: true,
      scale: true,
      axisPointer: oiValueAxisPointer(),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 9,
        formatter: (value: number) => formatCompact(value),
      },
      splitLine: { lineStyle: { color: '#172033', type: 'dashed' } },
    },
    dataZoom: [{
      id: 'screener-time',
        ...timeNavigation.zoom,
      type: 'inside',
      xAxisIndex: [0],
      zoomOnMouseWheel: true,
      moveOnMouseWheel: false,
      moveOnMouseMove: true,
      filterMode: 'none',
    }],
    series: [{
      name: exchange,
      type: 'line',
      showSymbol: derived.points.length === 1,
      smooth: false,
      connectNulls: false,
      lineStyle: { width: 1.4, color },
      itemStyle: { color },
      data: derived.series,
      markLine: chartAlertMarkLine(alertControls, symbol, exchange, 'oi'),
    }],
  }), [color, derived, exchange, interval, showXAxis, volumeLookup, alertControls, symbol, timeNavigation])

  useChartCrosshair(rulerChart, chartGroup, onCrosshairMove, interval)

  return (
    <div data-chart-collapsed={collapsed} className="relative overflow-hidden rounded-lg border border-slate-800/80 bg-[#080d16]">
      <div className="relative z-10 flex min-h-[34px] flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1.5">
        <div className="flex items-center gap-2">
          {orderControls}
          <ChartCollapseButton collapsed={collapsed} onToggle={onToggleCollapse} label={`${exchange} OI chart`} controlsId={chartBodyId} />
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300">{exchange}</span>
          <span className="hidden text-[9px] uppercase tracking-wide text-slate-600 xl:inline">Open Interest · Coins</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FundingRateBadge data={fundingData} />
          {displayValue != null && (
            <button type="button" onClick={fitVisibleScale}
              title="Click to fit OI scale to the visible time range"
              aria-label={`Fit ${exchange} OI scale to visible time range`}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-700/70 bg-slate-950/75 px-2 py-1 hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300">
              <span className="font-mono text-[11px] font-semibold" style={{ color }}>{formatCompact(displayValue)}</span>
              {hovered.ageSeconds != null && hovered.ageSeconds > 0 && (
                <span className="text-[9px] text-amber-400">{formatAge(hovered.ageSeconds)}</span>
              )}
              {hovered.point && !isFreshOI(hovered.point) && (
                <span className="text-[9px] uppercase text-amber-400">{hovered.point.filledFromTimestamp != null ? 'filled' : 'stale'}</span>
              )}
            </button>
          )}
        </div>
      </div>
      {!collapsed && derived.latestFilled && (
        <div className="pointer-events-none truncate px-3 text-[9px] text-amber-400"
          title={filledOIDescription(derived.latestFilled) || undefined}>
          Latest point filled with last data · {formatUtc(derived.latestFilled.filledFromTimestamp! * 1000)}
        </div>
      )}
      <div id={chartBodyId} className="relative" style={{ display: collapsed ? 'none' : undefined }}>
        <ReactECharts ref={chartRef} option={option} style={{ height: Math.max(80, height - 34), width: '100%' }} autoResize={false}
          onChartReady={(instance) => { instance.group = chartGroup; onChartReady(instance); setRulerChart(instance) }}
          replaceMerge={['series']} lazyUpdate />
        {!collapsed && <ChartRuler chart={rulerChart} series={rulerSeries} resetKey={`${exchange}:${interval}`} interval={interval} candleInterval={candleInterval} />}
        {!collapsed && <ChartLevelAlerts chart={rulerChart} metric="oi" series={alertSeries} controls={alertControls} />}
      </div>
    </div>
  )
}

export const OIPanel = memo(OIPanelComponent)
